import React, { useState, useCallback, useEffect } from 'react';
import moment from 'moment';
import logService from '../services/logService';
import documentService from '../services/documentService';

const { getIngredientsByLotCode } = logService;

const TH_STYLE = {
  position: 'sticky',
  top: 0,
  zIndex: 5,
  background: '#f8f9fa',
  boxShadow: 'inset 0 -1px 0 #dee2e6, 0 2px 6px rgba(0,0,0,0.1)',
};

export default function TraceabilityTable({ logs, filteredLogs, isFiltered, userRole, onEdit }) {
  const list = isFiltered ? filteredLogs : logs;
  const [expandedId, setExpandedId] = useState(null);
  const [ingredientCache, setIngredientCache] = useState({});
  const [bolDocsCache, setBolDocsCache] = useState({});
  const [expandedBolDocId, setExpandedBolDocId] = useState(null);

  // Fetch documents for all BOLs present in the current list
  useEffect(() => {
    const bolIds = [...new Set(list.map(l => l.BillOfLading?.id).filter(Boolean))];
    bolIds.forEach(bolId => {
      if (bolDocsCache[bolId] !== undefined) return;
      setBolDocsCache(c => ({ ...c, [bolId]: [] }));
      documentService.getDocumentsForBol(bolId)
        .then(docs => setBolDocsCache(c => ({ ...c, [bolId]: docs })))
        .catch(() => setBolDocsCache(c => ({ ...c, [bolId]: [] })));
    });
  }, [list]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalCols = 6 + (userRole === 'admin' ? 1 : 0); // date+product+lot+qty+customer+loggedBy + optional actions

  const handleToggle = useCallback(async (log) => {
    const id = log.id;
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    if (ingredientCache[id]) return;

    if (!log.batchId) {
      setIngredientCache(c => ({ ...c, [id]: [] }));
      return;
    }
    setIngredientCache(c => ({ ...c, [id]: 'loading' }));
    try {
      const data = await getIngredientsByLotCode(log.batchId, log.productId);
      const hasFlag = Array.isArray(data) && data.some(i => i.inProductRecipe !== undefined);
      const filtered = hasFlag ? data.filter(i => i.inProductRecipe) : data;
      setIngredientCache(c => ({ ...c, [id]: filtered }));
    } catch {
      setIngredientCache(c => ({ ...c, [id]: 'error' }));
    }
  }, [expandedId, ingredientCache]);

  return (
    <table className="table table-bordered table-responsive-sm mb-0">
      <thead>
        <tr>
          <th style={TH_STYLE}>Date</th>
          <th style={TH_STYLE}>Product</th>
          <th style={TH_STYLE}>Lot Code</th>
          <th style={TH_STYLE}>Quantity</th>
          <th style={TH_STYLE}>Customer</th>
          <th className="d-none d-md-table-cell" style={TH_STYLE}>Logged By</th>
          {userRole === 'admin' && <th style={TH_STYLE}>Actions</th>}
        </tr>
      </thead>
      <tbody>
        {list.map(log => {
          const isOpen = expandedId === log.id;
          const cached = ingredientCache[log.id];
          return (
            <React.Fragment key={log.id}>
              <tr style={{ cursor: 'pointer' }} onClick={() => handleToggle(log)}>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <span className="text-muted me-1" style={{ fontSize: 10 }}>{isOpen ? '▲' : '▼'}</span>
                  {moment.utc(log.date).format('MM/DD/YYYY')}
                </td>
                <td>
                  {log.Product?.name}
                  {log.isResale && (
                    <span className="badge bg-info text-dark ms-2" style={{ fontSize: 10, verticalAlign: 'middle' }}>Resale</span>
                  )}
                </td>
                <td>{log.lotCode}</td>
                <td>{log.quantity}</td>
                <td>{log.customer}</td>
                <td className="d-none d-md-table-cell">{log.logged_by}</td>
                {userRole === 'admin' && (
                  <td onClick={e => e.stopPropagation()}>
                    {!log.BillOfLading && (
                      <button className="btn btn-warning btn-sm" onClick={() => onEdit(log)}>Edit</button>
                    )}
                  </td>
                )}
              </tr>

              {/* BOL sub-row */}
              {log.BillOfLading && (() => {
                const bolId = log.BillOfLading.id;
                const bolDocs = bolDocsCache[bolId] || [];
                const docsOpen = expandedBolDocId === bolId;
                return (
                  <>
                    <tr style={{ backgroundColor: '#f0f7ff' }}>
                      <td colSpan={totalCols} style={{ paddingTop: 4, paddingBottom: 6, paddingLeft: 20, borderTop: 'none' }}>
                        <small className="text-muted me-2">Shipped via BOL:</small>
                        <a
                          href={`/bol/print?id=${bolId}`}
                          target="_blank"
                          rel="noreferrer"
                          className="font-monospace fw-semibold me-2"
                          style={{ fontSize: 13 }}
                          onClick={e => e.stopPropagation()}
                        >
                          {log.BillOfLading.bolNumber}
                        </a>
                        <span className={`badge ${log.BillOfLading.status === 'signed' ? 'bg-primary' : 'bg-success'}`}>
                          {log.BillOfLading.status}
                        </span>
                        {log.BillOfLading.scannedDocumentUrl && (
                          <a href={log.BillOfLading.scannedDocumentUrl} target="_blank" rel="noreferrer" className="ms-2 text-secondary small" title="View signed BOL" onClick={e => e.stopPropagation()}>📄 Signed BOL</a>
                        )}
                        {bolDocs.length > 0 && (
                          <button
                            className="btn btn-link btn-sm ms-2 p-0 text-decoration-none"
                            style={{ fontSize: 12 }}
                            onClick={e => { e.stopPropagation(); setExpandedBolDocId(docsOpen ? null : bolId); }}
                          >
                            📎 {bolDocs.length} document{bolDocs.length !== 1 ? 's' : ''} {docsOpen ? '▲' : '▼'}
                          </button>
                        )}
                      </td>
                    </tr>
                    {docsOpen && (
                      <tr style={{ backgroundColor: '#e8f0fe' }}>
                        <td colSpan={totalCols} style={{ paddingLeft: 32, paddingTop: 6, paddingBottom: 8, borderTop: 'none' }}>
                          <div className="d-flex flex-column gap-1">
                            {bolDocs.map(doc => {
                              return (
                                <div key={doc.id} className="d-flex align-items-center gap-2" style={{ fontSize: 12 }}>
                                  <span className="text-muted">{doc.category?.name || '—'}</span>
                                  <span className="fw-semibold">{doc.name}</span>
                                  <a href={doc.fileUrl} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} className="text-primary">⬇ Download</a>
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })()}

              {/* Invoice sub-row */}
              {log.CustomerOrder?.orderNumber && (
                <tr style={{ backgroundColor: '#f0faf0' }}>
                  <td colSpan={totalCols} style={{ paddingTop: 4, paddingBottom: 6, paddingLeft: 20, borderTop: 'none' }}>
                    <small className="text-muted me-2">Invoice:</small>
                    <span className="font-monospace fw-semibold me-2" style={{ fontSize: 13 }}>
                      {log.CustomerOrder.orderNumber}
                    </span>
                    {log.CustomerOrder.qbInvoiceNumber && (
                      log.CustomerOrder.qbInvoiceUrl
                        ? <a
                            href={log.CustomerOrder.qbInvoiceUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-success fw-semibold"
                            style={{ fontSize: 13 }}
                            onClick={e => e.stopPropagation()}
                          >
                            QB #{log.CustomerOrder.qbInvoiceNumber} ↗
                          </a>
                        : <span className="text-success fw-semibold" style={{ fontSize: 13 }}>
                            QB #{log.CustomerOrder.qbInvoiceNumber}
                          </span>
                    )}
                  </td>
                </tr>
              )}

              {/* Ingredients sub-row */}
              {isOpen && (
                <tr style={{ backgroundColor: '#fafafa' }}>
                  <td colSpan={totalCols} style={{ padding: 0, borderTop: 'none' }}>
                    {cached === 'loading' && (
                      <div className="px-4 py-2 text-muted small">Loading ingredients…</div>
                    )}
                    {cached === 'error' && (
                      <div className="px-4 py-2 text-danger small">Could not load ingredient data.</div>
                    )}
                    {Array.isArray(cached) && cached.length === 0 && (
                      <div className="px-4 py-2 text-muted small">
                        {log.isResale ? 'Resale product — no production records.' : 'No ingredient records for this lot code.'}
                      </div>
                    )}
                    {Array.isArray(cached) && cached.length > 0 && (
                      <table className="table table-sm mb-0" style={{ fontSize: 12 }}>
                        <thead className="table-light">
                          <tr>
                            <th className="ps-4">Ingredient</th>
                            <th>Lot Code</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cached.map((ing, i) => (
                            <tr key={i}>
                              <td className="ps-4">
                                {ing.Ingredient?.rawMaterialCode && (
                                  <span className="text-muted me-1 font-monospace" style={{ fontSize: 11 }}>{ing.Ingredient.rawMaterialCode}</span>
                                )}
                                {ing.Ingredient?.name}
                              </td>
                              <td className="font-monospace">{ing.ingredientLotCode || <span className="text-danger">Not recorded</span>}</td>
                              <td>
                                <span className={`badge bg-${ing.status === 'ENTERED' ? 'success' : ing.status === 'MISSING' ? 'danger' : 'secondary'}`} style={{ fontSize: 10 }}>
                                  {ing.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}
